import * as styles from "./ClientField.css";

import { useAtomValue, useSetAtom } from "jotai";
import { type JSX, useEffect, useMemo } from "react";

import {
  Combobox,
  ComboboxContent,
  ComboboxInput,
  ComboboxVirtualizedList,
  useComboboxVirtualizedScroll,
} from "@frontend/components/atoms/Combobox";
import type { ComboboxSize } from "@frontend/components/atoms/Combobox";
import { Spinner } from "@frontend/components/atoms/Spinner";
import { useLingui } from "@lingui/react/macro";

import { clientsAtom, clientsLoadingAtom, loadClientsAtom } from "../stores";
import type { ClientOption } from "../types";
import { ClientAvatar } from "./ClientAvatar";

export type ClientComboboxProps = {
  value: number | null | undefined;
  onValueChange: (client: ClientOption | null) => void;
  fallbackLabel?: string | null;
  disabled?: boolean;
  size?: ComboboxSize;
};

export function ClientCombobox({
  value: clientId,
  onValueChange,
  fallbackLabel,
  disabled,
  size,
}: ClientComboboxProps): JSX.Element {
  const { t } = useLingui();

  const clients = useAtomValue(clientsAtom);
  const loading = useAtomValue(clientsLoadingAtom);
  const loadClients = useSetAtom(loadClientsAtom);

  const { virtualizerRef, onItemHighlighted } =
    useComboboxVirtualizedScroll<ClientOption>();

  useEffect(() => {
    void loadClients();
  }, [loadClients]);

  const options = useMemo<ClientOption[]>(() => {
    if (null == clientId || clients.some((client) => client.id === clientId)) {
      return clients;
    }

    return [
      {
        id: clientId,
        label: fallbackLabel ?? String(clientId),
        contactId: null,
        isOrganization: null,
        avatarUrl: null,
        hasPhoto: false,
      },
      ...clients,
    ];
  }, [clients, clientId, fallbackLabel]);

  const selected = options.find((client) => client.id === clientId) ?? null;

  return (
    <Combobox
      virtualized
      items={options}
      value={selected}
      onValueChange={(client) => onValueChange(client ?? null)}
      itemToStringLabel={(client) => client?.label ?? ""}
      isItemEqualToValue={(a, b) => a.id === b.id}
      onItemHighlighted={onItemHighlighted}
      disabled={disabled}
      size={size}
    >
      <ComboboxInput
        placeholder={t`Choose a client`}
        leftSection={
          selected ? <ClientAvatar client={selected} size="auto" /> : null
        }
        loading={loading}
      />

      {/* A virtualized list has no measurable content, so the width is set. */}
      <ComboboxContent
        popupWidth="22rem"
        empty={
          loading ? (
            <span className={styles.loading}>
              <Spinner size={14} />
              {t`Loading`}
            </span>
          ) : (
            t`No match.`
          )
        }
      >
        <ComboboxVirtualizedList<ClientOption>
          estimateSize={() => 36}
          itemKey={(client) => String(client.id)}
          virtualizerRef={virtualizerRef}
        >
          {(client) => (
            <div className={styles.item}>
              <ClientAvatar
                client={client}
                size="auto"
                className={styles.itemAvatar}
              />

              <span className={styles.itemLabel}>{client.label}</span>
            </div>
          )}
        </ComboboxVirtualizedList>
      </ComboboxContent>
    </Combobox>
  );
}
